import { useEffect, RefObject, useRef } from 'react'
import { Box, Text } from '@machobear/design-system'
import { equals } from 'ramda'
import { DialogEventHandler } from '@/types'
import { PopupList as Menu, ListItem } from './PopupList.css'

type PopupListProps<T> = {
  options: { label: string; value: T }[]
  selectedValue: T
  open: boolean
  onClose: DialogEventHandler
  onChange: (value: T) => void
}

export const PopupList = <T,>({
  options,
  open,
  onClose,
  onChange,
  selectedValue
}: PopupListProps<T>) => {
  const ref = useRef<HTMLDivElement>(null)
  useClickOutside(ref, () => {
    onClose()
  })
  const onItemClick = (value: T) => {
    onChange(value)
    onClose()
  }

  return open ? (
    <Box
      ref={ref}
      component="ul"
      position="absolute"
      maxWidth="large"
      right={0}
      overflow="auto"
      zIndex={2}
      className={Menu}
      borderRadius={'standard'}
    >
      {options.map((option, i) => (
        <Box
          className={ListItem}
          component="li"
          cursor="pointer"
          padding="small"
          key={option.label}
          width="full"
          onClick={() => onItemClick(option.value)}
          style={{ whiteSpace: 'nowrap' }}
        >
          <Text weight={equals(selectedValue, option.value) ? 'strong' : 'regular'}>
            {option.label}
          </Text>
        </Box>
      ))}
    </Box>
  ) : null
}

type AnyEvent = MouseEvent | TouchEvent

function useClickOutside<T extends HTMLElement>(ref: RefObject<T>, callback: () => void) {
  useEffect(() => {
    const handleClickOutside = (event: AnyEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callback()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [ref, callback])

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        callback()
      }
    }

    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [callback])
}

export default useClickOutside
